import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import './AgentAnalytics.css';

const AgentAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30'); // Default to 30 days
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDates, setShowCustomDates] = useState(false);
  
  // Analytics data states
  const [dashboardData, setDashboardData] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [supplierPerformance, setSupplierPerformance] = useState([]);
  const [quoteConversion, setQuoteConversion] = useState(null);
  const [clientDemographics, setClientDemographics] = useState(null);
  const [error, setError] = useState(null);

  // Period options
  const periodOptions = [
    { value: '1', label: 'Today' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 3 Months' },
    { value: '365', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = period === 'custom' 
        ? { start: customDateRange.start, end: customDateRange.end }
        : { period };

      // Fetch all analytics in parallel
      const [dashboard, revenue, supplier, conversion, demographics] = await Promise.all([
        analyticsService.getDashboard(params),
        analyticsService.getRevenueTrends(params),
        analyticsService.getSupplierPerformance(params),
        analyticsService.getQuoteConversion(params),
        analyticsService.getClientDemographics(params)
      ]);

      setDashboardData(dashboard);
      setRevenueTrends(revenue);
      setSupplierPerformance(supplier);
      setQuoteConversion(conversion);
      setClientDemographics(demographics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when period changes
  useEffect(() => {
    if (period !== 'custom' || (customDateRange.start && customDateRange.end)) {
      fetchAnalytics();
    }
  }, [period, customDateRange]);

  // Handle period change
  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setPeriod(value);
    setShowCustomDates(value === 'custom');
  };

  // Calculate conversion rate
  const getConversionRate = () => {
    if (!quoteConversion || !quoteConversion.total_quotes || quoteConversion.total_quotes === 0) {
      return '0';
    }
    return ((quoteConversion.converted_quotes / quoteConversion.total_quotes) * 100).toFixed(1);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="agent-analytics-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-analytics-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <h1>My Analytics</h1>
        <div className="period-selector">
          <select value={period} onChange={handlePeriodChange} className="period-select">
            {periodOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {showCustomDates && (
            <div className="custom-date-range">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                className="date-input"
              />
              <span>to</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                className="date-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Analytics Cards Grid */}
      <div className="analytics-grid">
        
        {/* 1. Dashboard Overview Card */}
        <div className="analytics-card">
          <h2>Performance Overview</h2>
          <div className="metrics-grid">
            <div className="metric">
              <span className="metric-value">{dashboardData?.quotes?.total_quotes || 0}</span>
              <span className="metric-label">Total Quotes</span>
            </div>
            <div className="metric">
              <span className="metric-value">{dashboardData?.quotes?.sent_quotes || 0}</span>
              <span className="metric-label">Sent Quotes</span>
            </div>
            <div className="metric">
              <span className="metric-value">{dashboardData?.quotes?.accepted_quotes || 0}</span>
              <span className="metric-label">Accepted Quotes</span>
            </div>
            <div className="metric">
              <span className="metric-value">{formatCurrency(dashboardData?.quotes?.total_quote_value)}</span>
              <span className="metric-label">Total Quote Value</span>
            </div>
          </div>
        </div>

        {/* 2. Commission Stats Card */}
        <div className="analytics-card">
          <h2>Commission Summary</h2>
          <div className="metrics-grid">
            <div className="metric">
              <span className="metric-value">{dashboardData?.commissions?.total_commissions || 0}</span>
              <span className="metric-label">Total Commissions</span>
            </div>
            <div className="metric">
              <span className="metric-value">{dashboardData?.commissions?.approved_commissions || 0}</span>
              <span className="metric-label">Approved</span>
            </div>
            <div className="metric highlight">
              <span className="metric-value">{formatCurrency(dashboardData?.commissions?.total_earnings)}</span>
              <span className="metric-label">Total Earnings</span>
            </div>
            <div className="metric">
              <span className="metric-value">{formatCurrency(dashboardData?.commissions?.pending_earnings)}</span>
              <span className="metric-label">Pending Earnings</span>
            </div>
          </div>
        </div>

        {/* 3. Client Demographics Card */}
        <div className="analytics-card">
          <h2>Client Demographics</h2>
          <div className="demographics-content">
            <div className="demographic-item">
              <span className="demo-label">Total Clients</span>
              <span className="demo-value">{clientDemographics?.total_clients || 0}</span>
            </div>
            <div className="demographic-item">
              <span className="demo-label">US Clients</span>
              <span className="demo-value">{clientDemographics?.us_clients || 0}</span>
            </div>
            <div className="demographic-item">
              <span className="demo-label">International Clients</span>
              <span className="demo-value">{clientDemographics?.international_clients || 0}</span>
            </div>
          </div>
        </div>

        {/* 4. Quote Conversion Card */}
        <div className="analytics-card">
          <h2>Quote Conversion</h2>
          <div className="conversion-content">
            <div className="conversion-rate">
              <span className="rate-value">{getConversionRate()}%</span>
              <span className="rate-label">Conversion Rate</span>
            </div>
            <div className="conversion-details">
              <div className="detail-item">
                <span className="detail-label">Total Quotes</span>
                <span className="detail-value">{quoteConversion?.total_quotes || 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Converted</span>
                <span className="detail-value">{quoteConversion?.converted_quotes || 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Avg Days to Convert</span>
                <span className="detail-value">{Math.round(quoteConversion?.avg_conversion_days || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Revenue Trends Card */}
        <div className="analytics-card full-width">
          <h2>Revenue Trends</h2>
          <div className="revenue-trends">
            {revenueTrends.length > 0 ? (
              <div className="trends-table">
                <div className="trends-header">
                  <span>Month</span>
                  <span>Revenue</span>
                  <span>Commissions</span>
                </div>
                {revenueTrends.map((trend, index) => (
                  <div key={index} className="trend-row">
                    <span>{trend.month}</span>
                    <span>{formatCurrency(trend.revenue)}</span>
                    <span>{trend.commission_count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No revenue data available for this period</p>
            )}
          </div>
        </div>

        {/* 6. Supplier Performance Card */}
        <div className="analytics-card full-width">
          <h2>Top Suppliers</h2>
          <div className="supplier-performance">
            {supplierPerformance.length > 0 ? (
              <div className="supplier-table">
                <div className="supplier-header">
                  <span>Supplier</span>
                  <span>Bookings</span>
                  <span>Total Commission</span>
                  <span>Avg Commission</span>
                </div>
                {supplierPerformance.map((supplier, index) => (
                  <div key={index} className="supplier-row">
                    <span>{supplier.supplier || 'Unknown'}</span>
                    <span>{supplier.booking_count}</span>
                    <span>{formatCurrency(supplier.total_commission)}</span>
                    <span>{formatCurrency(supplier.avg_commission)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No supplier data available for this period</p>
            )}
          </div>
        </div>

        {/* 7. Travel Status Card */}
        <div className="analytics-card">
          <h2>Travel Status</h2>
          <div className="metrics-grid">
            <div className="metric">
              <span className="metric-value">{dashboardData?.itineraries?.total_itineraries || 0}</span>
              <span className="metric-label">Total Itineraries</span>
            </div>
            <div className="metric">
              <span className="metric-value">{dashboardData?.itineraries?.upcoming_travel || 0}</span>
              <span className="metric-label">Upcoming</span>
            </div>
            <div className="metric">
              <span className="metric-value">{dashboardData?.itineraries?.active_travel || 0}</span>
              <span className="metric-label">Active</span>
            </div>
            <div className="metric">
              <span className="metric-value">{dashboardData?.itineraries?.completed_travel || 0}</span>
              <span className="metric-label">Completed</span>
            </div>
          </div>
        </div>

        {/* 8. Clients Card */}
        <div className="analytics-card">
          <h2>Client Activity</h2>
          <div className="client-activity">
            <div className="activity-stat">
              <span className="stat-value">{dashboardData?.clients?.total_clients || 0}</span>
              <span className="stat-label">New Clients in Period</span>
            </div>
            <div className="activity-stat">
              <span className="stat-value">{clientDemographics?.total_clients || 0}</span>
              <span className="stat-label">Total Active Clients</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AgentAnalytics;
